import { useState, useEffect, useCallback } from "react";
import { placeholderSvg, stoneImage } from "../utils/stoneImage";

const API_URL    = process.env.REACT_APP_API_SEARCH_FILTER_URL || "http://127.0.0.1:8000/search/by-filters";

// Note: The /search/by-filters endpoint accepts structured filters directly,
// bypassing LLM re-extraction. This ensures "View All" shows the same filtered
// results as the chat, not a re-interpreted broader set.

// ── Stone Card (same as in BotResponse) ────────────────────────────────────────
function StoneCard({ stone }) {
  const s = stone;
  const specs = [s.cut, s.polish, s.symmetry].filter(Boolean).join(" / ");

  const statusColor =
    s.status === "A" ? "text-emerald-600" :
      s.status === "H" ? "text-amber-500" :
        s.status === "M" ? "text-blue-500" : "text-gray-400";

  const statusLabel =
    s.status === "A" ? "Available" :
      s.status === "H" ? "On Hold" :
        s.status === "M" ? "On Memo" :
          s.status || "—";

  const colorDisplay = s.is_fancy_color
    ? [s.fancy_intensity, s.fancy_color].filter(Boolean).join(" ")
    : s.color;

  // Real photo only when the link is this stone's own; otherwise a labelled sample
  const image = stoneImage(s);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col">
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 h-32 flex items-center justify-center relative overflow-hidden">
        <img
          src={image.src}
          alt={s.stock_id}
          className="h-full w-full object-contain"
          onError={(e) => {
            // Photo or sample file failed to load → drawn placeholder
            e.target.onerror = null;
            e.target.src = placeholderSvg(s.is_fancy_color, s.fancy_color);
          }}
        />
        {!image.isReal && (
          <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 bg-white/90 text-gray-500 text-[10px] px-1.5 py-0.5 rounded">
            Sample image
          </span>
        )}
        {s.lab && (
          <span className="absolute top-2 left-2 bg-white border border-gray-200 text-xs font-bold px-1.5 py-0.5 rounded shadow-sm">
            {s.lab}
          </span>
        )}
        {s.is_fancy_color && (
          <span className="absolute top-2 right-2 bg-yellow-100 text-yellow-700 text-xs font-semibold px-1.5 py-0.5 rounded">
            Fancy
          </span>
        )}
      </div>

      <div className="p-3 flex flex-col gap-1 flex-1">
        <div className="flex justify-between items-center">
          <span className="text-xs font-mono text-gray-400">{s.stock_id || "—"}</span>
          <span className={`text-xs font-semibold ${statusColor}`}>{statusLabel}</span>
        </div>

        <div className="text-sm font-semibold text-gray-900 leading-snug">
          {s.shape} {s.carat}ct {colorDisplay} {s.clarity}
        </div>

        {specs && <div className="text-xs text-gray-500">{specs}</div>}

        {s.fluorescence && s.fluorescence !== "NON" && (
          <div className="text-xs text-gray-400">Flu: {s.fluorescence}</div>
        )}

        {s.measurements && (
          <div className="text-xs text-gray-400">{s.measurements}</div>
        )}

        {/* {s.location && (
          <div className="text-xs text-gray-400">📍 {s.location}</div>
        )} */}

        <div className="mt-auto pt-2 flex justify-between items-end border-t border-gray-100">
          {s.price_per_carat > 0 && (
            <div className="text-xs text-gray-500">
              ${Number(s.price_per_carat).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/ct
            </div>
          )}
          {s.total_price > 0 && (
            <div className="text-base font-bold text-gray-900">
              ${Number(s.total_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          )}
        </div>

        {(s.certificate_url || image.isReal || s.video_url || s.has_certificate || s.has_video) && (
          <div className="flex gap-3 mt-1">
            {image.isReal && (
              <a href={s.img_url} target="_blank" rel="noreferrer"
                className="text-xs text-blue-600 hover:underline">📸 Image</a>
            )}
            {s.video_url && (
              <a href={s.video_url} target="_blank" rel="noreferrer"
                className="text-xs text-blue-600 hover:underline">▶ Video</a>
            )}
            {s.certificate_url && (
              <a href={s.certificate_url} target="_blank" rel="noreferrer"
                className="text-xs text-blue-600 hover:underline">📄 Cert</a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


export default function SearchResults({ filters, onBack }) {
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const PAGE_SIZE = 30;

  // Strip LLM metadata fields that aren't actual diamond filters
  const cleanFilters = useCallback((f) => {
    const cleaned = { ...f };
    delete cleaned.confidence;
    delete cleaned.unrecognized_terms;
    delete cleaned.limit;
    return cleaned;
  }, []);

  const fetchResults = useCallback(async (offset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filters: cleanFilters(filters),
          limit: PAGE_SIZE,
          offset,
        }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();

      if (offset === 0) {
        setResults(data.results || []);
      } else {
        setResults(prev => [...prev, ...(data.results || [])]);
      }
      setTotal(data.total || 0);
      setHasMore((data.results?.length || 0) === PAGE_SIZE);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, cleanFilters]);

  useEffect(() => {
    fetchResults(0);
  }, [fetchResults]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchResults(nextPage * PAGE_SIZE);
  };

  // Build filter summary for display
  const filterSummary = [];
  // A filter can hold one value ("lab") or several ("labs") — show either
  const addMulti = (label, single, list) => {
    const values = list?.length ? list : single ? [single] : [];
    if (values.length) filterSummary.push({ label, value: values.join(", ") });
  };
  if (filters.shape) filterSummary.push({ label: "Shape", value: filters.shape });
  if (filters.shapes?.length) filterSummary.push({ label: "Shapes", value: filters.shapes.join(", ") });
  if (filters.carat_values?.length) filterSummary.push({ label: "Carat", value: filters.carat_values.map((c) => `~${c}ct`).join(", ") });
  else if (filters.carat_exact) filterSummary.push({ label: "Carat", value: `${filters.carat_exact}ct` });
  else if (filters.carat_min || filters.carat_max) {
    const lo = filters.carat_min ? `${filters.carat_min}` : "0";
    const hi = filters.carat_max ? `${filters.carat_max}` : "∞";
    filterSummary.push({ label: "Carat", value: `${lo}–${hi}ct` });
  }
  if (filters.color) filterSummary.push({ label: "Color", value: filters.color });
  if (filters.colors?.length) filterSummary.push({ label: "Colors", value: filters.colors.join(", ") });
  if (filters.clarity) filterSummary.push({ label: "Clarity", value: filters.clarity });
  addMulti("Lab", filters.lab, filters.labs);
  addMulti("Cut", filters.cut, filters.cuts);
  addMulti("Polish", filters.polish, filters.polishes);
  addMulti("Symmetry", filters.symmetry, filters.symmetries);
  if (filters.diamond_status) {
    // "A,M" when several statuses were asked for
    const statusMap = { A: "Available", H: "On Hold", M: "On Memo", S: "Sold", ALL: "All" };
    const value = filters.diamond_status.split(",").map((s) => statusMap[s] || s).join(", ");
    filterSummary.push({ label: "Status", value });
  }
  addMulti("Fluorescence", filters.fluorescence, filters.fluorescences);
  if (filters.table_pct_min != null || filters.table_pct_max != null) {
    const lo = filters.table_pct_min ?? "0";
    const hi = filters.table_pct_max ?? "∞";
    filterSummary.push({ label: "Table%", value: `${lo}–${hi}%` });
  }
  if (filters.depth_pct_min != null || filters.depth_pct_max != null) {
    const lo = filters.depth_pct_min ?? "0";
    const hi = filters.depth_pct_max ?? "∞";
    filterSummary.push({ label: "Depth%", value: `${lo}–${hi}%` });
  }
  if (filters.is_fancy_color) filterSummary.push({ label: "Type", value: "Fancy Color" });
  addMulti("Intensity", filters.fancy_intensity, filters.fancy_intensities);
  addMulti("Fancy Color", filters.fancy_color, filters.fancy_colors);
  addMulti("Location", filters.location, filters.locations);
  addMulti("Country", filters.country, filters.countries);
  // Every other filter the search used gets a chip too — a missing one reads as "not applied"
  const span = (lo, hi, unit = "", fmt = (v) => v) =>
    lo != null && hi != null ? `${fmt(lo)}–${fmt(hi)}${unit}`
      : lo != null ? `${fmt(lo)}${unit}+` : `up to ${fmt(hi)}${unit}`;
  const money = (v) => `$${Number(v).toLocaleString()}`;
  const grades = (best, worst) => (best && worst ? `${best}–${worst}` : worst ? `${worst} or better` : `${best} or lower`);
  if (filters.shapes_excluded?.length) filterSummary.push({ label: "Not Shape", value: filters.shapes_excluded.join(", ") });
  if (filters.labs_excluded?.length) filterSummary.push({ label: "Not Lab", value: filters.labs_excluded.join(", ") });
  if (filters.color_best || filters.color_worst)
    filterSummary.push({ label: "Color", value: grades(filters.color_best, filters.color_worst) });
  if (filters.clarity_best || filters.clarity_worst)
    filterSummary.push({ label: "Clarity", value: grades(filters.clarity_best, filters.clarity_worst) });
  if (filters.price_min != null || filters.price_max != null)
    filterSummary.push({ label: "Price", value: span(filters.price_min, filters.price_max, "", money) });
  // back_min -40 = at least 40% off Rap, back_max -60 = at most 60% off
  if (filters.back_min != null || filters.back_max != null) {
    const lo = filters.back_min != null ? Math.abs(filters.back_min) : null;
    const hi = filters.back_max != null ? Math.abs(filters.back_max) : null;
    filterSummary.push({ label: "Discount", value: `${span(lo, hi, "%")} off Rap` });
  }
  if (filters.price_per_carat_min != null || filters.price_per_carat_max != null)
    filterSummary.push({ label: "Price/ct", value: span(filters.price_per_carat_min, filters.price_per_carat_max, "", money) });
  if (filters.length_min != null || filters.length_max != null)
    filterSummary.push({ label: "Length", value: span(filters.length_min, filters.length_max, "mm") });
  if (filters.width_min != null || filters.width_max != null)
    filterSummary.push({ label: "Width", value: span(filters.width_min, filters.width_max, "mm") });
  if (filters.ratio_min != null || filters.ratio_max != null)
    filterSummary.push({ label: "Ratio", value: span(filters.ratio_min, filters.ratio_max) });
  if (filters.is_fancy_color === false) filterSummary.push({ label: "Type", value: "No fancy colors" });
  if (filters.no_bgm) filterSummary.push({ label: "BGM", value: "None" });
  if (filters.no_black_table) filterSummary.push({ label: "Black in table", value: "None" });

  return (
    <div className="flex flex-col h-screen flex-1 min-w-0 bg-gray-50">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-white flex items-center gap-3 shadow-sm">
        <button
          onClick={onBack}
          className="text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium flex items-center gap-1"
        >
          ← Back to Chat
        </button>
        <div className="h-5 w-px bg-gray-200" />
        <div>
          <div className="font-semibold text-gray-900 text-sm">
            Search Results
            {total > 0 && (
              <span className="text-gray-400 font-normal ml-2">({total.toLocaleString()} stones)</span>
            )}
          </div>
        </div>
      </div>

      {/* Active filters */}
      {filterSummary.length > 0 && (
        <div className="px-6 py-3 bg-white border-b flex flex-wrap gap-2 items-center">
          <span className="text-xs text-gray-400 font-medium">Filters:</span>
          {filterSummary.map((f, i) => (
            <span key={i} className="inline-flex items-center bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full border border-gray-200">
              <span className="font-medium text-gray-500 mr-1">{f.label}:</span>
              {f.value}
            </span>
          ))}
        </div>
      )}

      {/* Results */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {error && (
          <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
            <span>⚠️</span>
            <span>Error loading results: {error}</span>
          </div>
        )}

        {!loading && results.length === 0 && !error && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <span className="text-5xl mb-3">💎</span>
            <p className="text-sm font-medium">No diamonds found matching your filters</p>
            <button
              onClick={onBack}
              className="mt-3 text-sm text-blue-600 hover:underline"
            >
              Go back and try a different search
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {results.map((s, i) => (
            <StoneCard key={s.stock_id || i} stone={s} />
          ))}
        </div>

        {/* Load more */}
        {hasMore && !loading && (
          <div className="flex justify-center mt-6">
            <button
              onClick={loadMore}
              className="inline-flex items-center gap-1.5 bg-black text-white text-sm font-medium px-6 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Load more stones
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center py-10">
            <div className="flex gap-1.5 items-center">
              {[0, 150, 300].map((delay) => (
                <span
                  key={delay}
                  className="w-2.5 h-2.5 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}